import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  ContactDetailsCreditorDto,
  ContactDetailsCustomerDto,
  ContactDetailsDebtorDto,
  ContactDetailsDto,
  ContactDetailsEmployeeDto,
  ContactDetailsFinanceCompanyDto,
  ContactMergeDto,
  ContactRestService,
  ContactRoleType,
  ContactType,
  CreditorType,
  SupplierCategoryType,
  SupplierServiceType,
} from '@gh/builder-shared-data';
import { ID } from '@gh/common-shared-data';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { cloneDeep, isEqual } from 'lodash';
import { DialogService, NotificationService, yesAnswer, yesNoConfirmation } from '@gh/core-ui';
import { Enum } from '@gh/core-data';

export interface ContactMergeDialogData {
  duplicateContactId: ID;
  primaryContactId: ID;
  primaryHasXeroContact?: boolean;
  duplicateHasXeroContact?: boolean;
}

export interface ContactUsage {
  jobsCount: number;
  salesFoldersCount: number;
}

@Component({
  standalone: false,
  selector: 'gh-contact-merge-dialog',
  templateUrl: './contact-merge-dialog.component.html',
  styleUrls: ['./contact-merge-dialog.component.scss'],
})
export class ContactMergeDialogComponent implements OnInit, OnDestroy {
  originalPrimaryContact: ContactDetailsDto;
  originalDuplicateContact: ContactDetailsDto;
  originalPrimaryUsage: ContactUsage = { jobsCount: 0, salesFoldersCount: 0 };
  originalDuplicateUsage: ContactUsage = { jobsCount: 0, salesFoldersCount: 0 };
  primaryNominated: 'primary' | 'duplicate' = 'primary';
  overriddenFields: Set<string> = new Set();

  loading = true;
  merging = false;
  showLearnMore = false;
  showAllFields = false;

  // Fields to compare using full paths, grouped by sections
  sections = [
    {
      id: 'common',
      title: 'lbl_merge_common_section',
      icon: 'gh:contacts',
      expanded: true,
      fields: [
        { key: 'common.firstName', label: 'lbl_contact_first_name' },
        { key: 'common.lastName', label: 'lbl_contact_last_name' },
        { key: 'common.businessName', label: 'lbl_business_name' },
        { key: 'common.displayNameAs', label: 'lbl_contact_display' },
        { key: 'common.addressee', label: 'lbl_contact_addresse' },
        { key: 'common.street1', label: 'lbl_street1' },
        { key: 'common.street2', label: 'lbl_street2' },
        { key: 'common.city', label: 'lbl_city' },
        { key: 'common.state', label: 'lbl_state' },
        { key: 'common.postcode', label: 'lbl_zip' },
        { key: 'common.phone1', label: 'lbl_phone1' },
        { key: 'common.phone2', label: 'lbl_phone2' },
        { key: 'common.fax', label: 'lbl_fax' },
        { key: 'common.email', label: 'lbl_primary_email' },
        { key: 'common.otherEmails', label: 'lbl_other_emails' },
        { key: 'common.salutation', label: 'lbl_contact_salutation' },
        { key: 'common.contact', label: 'lbl_contact' },
        { key: 'common.memo', label: 'lbl_memo' }
      ],
      conflicts: [] as any[],
      visible: true
    },
    {
      id: 'customer',
      title: 'lbl_merge_customer_section',
      icon: 'gh:customer',
      expanded: false,
      fields: [
        { key: 'customer.common.referredBy.referredBy', label: 'lbl_referred_by' },
        { key: 'customer.common.referredBy.referredDetails', label: 'lbl_referred_by_details' },
        { key: 'customer.common.referredBy.marketingOptIn', label: 'lbl_marketing_opt' },
        { key: 'customer.common.billing.deliveryBy', label: 'lbl_merge_billing_delivery' },
        { key: 'customer.common.billing.email', label: 'lbl_merge_billing_email' },
        { key: 'customer.common.billing.addressee', label: 'lbl_merge_billing_addressee' },
        { key: 'customer.common.billing.street1', label: 'lbl_merge_billing_street1' },
        { key: 'customer.common.billing.city', label: 'lbl_merge_billing_city' },
        { key: 'customer.additionalDetails.dateOfBirth', label: 'lbl_dob' },
        { key: 'customer.additionalDetails.websiteLead', label: 'lbl_website_lead' }
      ],
      conflicts: [] as any[],
      visible: false
    },
    {
      id: 'employee',
      title: 'lbl_merge_employee_section',
      icon: 'gh:employee',
      expanded: false,
      fields: [
        { key: 'employee.common.personal.nameToPrintOnChecks', label: 'lbl_name_to_print_checks' },
        { key: 'employee.common.personal.dateOfBirth', label: 'lbl_dob' },
        { key: 'employee.common.personal.startDate', label: 'lbl_start_date' },
        { key: 'employee.common.personal.terminationDate', label: 'lbl_end_date' },
        { key: 'employee.common.salary.payBasis', label: 'lbl_pay_basis' },
        { key: 'employee.common.salary.payrollCycle', label: 'lbl_payroll_cycle' },
        { key: 'employee.common.salary.hoursInPayPeriod', label: 'lbl_hours_in_pay_period' },
        { key: 'employee.common.salary.salary', label: 'lbl_salary' },
        { key: 'employee.common.salary.hourlyRate', label: 'lbl_hourly_rate' },
        { key: 'employee.common.salary.commissionRate', label: 'lbl_commission_rate' },
      ],
      conflicts: [] as any[],
      visible: false
    },
    {
      id: 'supplier',
      title: 'lbl_merge_supplier_section',
      icon: 'gh:supplier',
      expanded: false,
      fields: [
        { key: 'creditorSupplier.common.provides', label: 'lbl_provides' },
        { key: 'creditorSupplier.common.category', label: 'lbl_category' },
        { key: 'creditorSupplier.common.businessInfo.abn', label: 'lbl_abn' },
        { key: 'creditorSupplier.common.businessInfo.gst', label: 'lbl_gst' },
        { key: 'creditorSupplier.common.businessInfo.taxNumber', label: 'lbl_tax_ein' },
        { key: 'creditorSupplier.common.businessInfo.socialNumber', label: 'lbl_social_number' }
      ],
      conflicts: [] as any[],
      visible: false
    },
    {
      id: 'debtor',
      title: 'lbl_merge_debtor_section',
      icon: 'gh:debtor',
      expanded: false,
      fields: [
        { key: 'debtor.common.creditLimit', label: 'lbl_credit_limit' },
        { key: 'debtor.common.creditLimitNotify', label: 'lbl_credit_limit_notify' }
      ],
      conflicts: [] as any[],
      visible: false
    },
    {
      id: 'financeCompany',
      title: 'lbl_merge_finance_company_section',
      icon: 'gh:finance',
      expanded: false,
      fields: [
        { key: 'financeCompany.common.financeCompanyType', label: 'lbl_finance_company_type' }
      ],
      conflicts: [] as any[],
      visible: false
    }
  ];

  conflicts: any[] = [];
  selection: { [key: string]: 'primary' | 'duplicate' } = {};
  private visibleConflictsBySection: { [sectionId: string]: any[] } = {};
  private differentFieldsCountBySection: { [sectionId: string]: number } = {};
  private readonly destroy$ = new Subject<void>();

  constructor(
    private dialogRef: MatDialogRef<ContactMergeDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ContactMergeDialogData,
    private contactService: ContactRestService,
    private notificationService: NotificationService,
    private dialogService: DialogService
  ) {}

  ngOnInit(): void {
    this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadData(): void {
    this.loading = true;
    this.contactService.getCompare(this.data.primaryContactId, this.data.duplicateContactId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ primaryContact, duplicateContact }) => {
          this.originalPrimaryContact = primaryContact;
          this.originalDuplicateContact = duplicateContact;

          this.calculateUsage(this.originalDuplicateContact, this.originalDuplicateUsage);
          this.calculateUsage(this.originalPrimaryContact, this.originalPrimaryUsage);

          this.identifyConflicts();
          this.loading = false;
        },
        error: (error) => {
          this.loading = false;
          this.notificationService.processAll(error);
        }
      });
  }

  calculateUsage(contact: ContactDetailsDto, usage: ContactUsage): void {
    if (contact.customer && contact.customer.communication && contact.customer.communication.communications) {
      const salesFolderIds = contact.customer.communication.communications
        .filter(c => c.salesFolderId)
        .map(c => c.salesFolderId);
      usage.salesFoldersCount = new Set(salesFolderIds).size;

      const jobIds = contact.customer.communication.communications
        .filter(c => c.jobId)
        .map(c => c.jobId);
      usage.jobsCount = new Set(jobIds).size;
    } else {
      usage.salesFoldersCount = 0;
      usage.jobsCount = 0;
    }
  }

  allFieldsMatch = false;

  getRawValueByPath(obj: any, path: string): any {
    if (!obj) return undefined;
    // Individual name fields live under common.individualName, not common.firstName/lastName
    if (path === 'common.firstName') {
      return obj.common?.individualName?.firstName;
    }
    if (path === 'common.lastName') {
      return obj.common?.individualName?.lastName;
    }
    const parts = path.split('.');
    let current = obj;
    for (const part of parts) {
      if (current === null || current === undefined) return undefined;
      current = current[part];
    }
    return current;
  }

  getValueByPath(obj: any, path: string): any {
    const current = this.getRawValueByPath(obj, path);
    if (current && typeof current === 'object') {
      if ('localization' in current && current.localization) {
        return current.localization;
      }
      if ('name' in current && current.name) {
        return current.name;
      }
    }
    return current;
  }

  setValueByPath(obj: any, path: string, value: any): void {
    if (!obj) return;
    if (path === 'common.firstName') {
      if (!obj.common) obj.common = {};
      if (!obj.common.individualName) obj.common.individualName = {};
      obj.common.individualName.firstName = value;
      return;
    }
    if (path === 'common.lastName') {
      if (!obj.common) obj.common = {};
      if (!obj.common.individualName) obj.common.individualName = {};
      obj.common.individualName.lastName = value;
      return;
    }
    const parts = path.split('.');
    let current = obj;
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      if (i === parts.length - 1) {
        current[part] = value;
      } else {
        if (!current[part]) {
          current[part] = {};
        }
        current = current[part];
      }
    }
  }

  getDisplayName(contact: ContactDetailsDto): string {
    if (!contact || !contact.common) return '';
    if (contact.common.type === ContactRoleType.Business) {
      return contact.common.businessName || '';
    }
    const first = contact.common.individualName?.firstName || '';
    const last = contact.common.individualName?.lastName || '';
    return `${first} ${last}`.trim();
  }

  setPrimaryNominated(choice: 'primary' | 'duplicate'): void {
    if (this.primaryNominated === choice) {
      return;
    }
    this.primaryNominated = choice;

    // Update selections for fields NOT overridden by user
    this.conflicts.forEach(conflict => {
      if (!this.overriddenFields.has(conflict.key)) {
        this.selection[conflict.key] = choice;
      }
    });
  }

  selectAll(choice: 'primary' | 'duplicate'): void {
    this.conflicts.forEach(conflict => {
      this.selection[conflict.key] = choice;
      this.overriddenFields.add(conflict.key);
    });
  }

  private normalizeForComparison(val: any): any {
    if (val === null || val === undefined) {
      return null;
    }
    if (typeof val === 'string') {
      const trimmed = val.trim();
      return trimmed === '' ? null : trimmed;
    }
    if (typeof val === 'object') {
      if ('itemCode' in val && val.itemCode !== undefined) {
        return val.itemCode;
      }
      if ('localization' in val && val.localization !== undefined) {
        return val.localization;
      }
      if ('name' in val && val.name !== undefined) {
        return val.name;
      }
      if ('id' in val && val.id !== undefined) {
        return val.id;
      }
    }
    return val;
  }

  hasContactType(contact: ContactDetailsDto, typeCode: string): boolean {
    if (!contact || !contact.common || !contact.common.contactTypes) return false;
    const typeObj = contact.common.contactTypes.find(t => {
      const dbCode = t.type ? (Enum.toValue(ContactType, t.type) || t.type) : null;
      return dbCode === typeCode;
    });
    return !!(typeObj && typeObj.assigned && typeObj.visible && typeObj.active);
  }

  identifyConflicts(): void {
    this.sections.forEach(sec => {
      sec.conflicts = [];
      if (sec.id === 'common') {
        sec.visible = true;
      } else if (sec.id === 'customer') {
        sec.visible = this.hasContactType(this.originalPrimaryContact, 'CT') || this.hasContactType(this.originalDuplicateContact, 'CT');
      } else if (sec.id === 'employee') {
        sec.visible = this.hasContactType(this.originalPrimaryContact, 'EE') || this.hasContactType(this.originalDuplicateContact, 'EE');
      } else if (sec.id === 'supplier') {
        sec.visible = this.hasContactType(this.originalPrimaryContact, 'CS') || this.hasContactType(this.originalDuplicateContact, 'CS');
      } else if (sec.id === 'debtor') {
        sec.visible = this.hasContactType(this.originalPrimaryContact, 'DB') || this.hasContactType(this.originalDuplicateContact, 'DB');
      } else if (sec.id === 'financeCompany') {
        sec.visible = this.hasContactType(this.originalPrimaryContact, 'FC') || this.hasContactType(this.originalDuplicateContact, 'FC');
      }

      if (sec.visible) {
        sec.fields.forEach(field => {
          const dupValue = this.getValueByPath(this.originalDuplicateContact, field.key);
          const primValue = this.getValueByPath(this.originalPrimaryContact, field.key);
          const dupRaw = this.getRawValueByPath(this.originalDuplicateContact, field.key);
          const primRaw = this.getRawValueByPath(this.originalPrimaryContact, field.key);

          if (dupValue !== undefined || primValue !== undefined) {
            const normDup = this.normalizeForComparison(dupRaw !== undefined ? dupRaw : dupValue);
            const normPrim = this.normalizeForComparison(primRaw !== undefined ? primRaw : primValue);
            const isMatch = isEqual(normDup, normPrim);
            sec.conflicts.push({
              ...field,
              duplicateValue: dupValue,
              primaryValue: primValue,
              duplicateRaw: dupRaw,
              primaryRaw: primRaw,
              isMatch: isMatch
            });
            if (this.selection[field.key] === undefined) {
              this.selection[field.key] = this.primaryNominated;
            }
          }
        });
      }
    });

    this.conflicts = this.sections.filter(s => s.visible).reduce((acc, s) => acc.concat(s.conflicts), [] as any[]);
    this.allFieldsMatch = this.conflicts.length > 0 && this.conflicts.every(c => c.isMatch);
    this.refreshConflictCaches();
  }

  getVisibleConflictsForSection(sectionId: string): any[] {
    return this.visibleConflictsBySection[sectionId] || [];
  }

  getDifferentFieldsCountForSection(sectionId: string): number {
    return this.differentFieldsCountBySection[sectionId] || 0;
  }

  selectAllForSection(sectionId: string, choice: 'primary' | 'duplicate'): void {
    const conflicts = this.getVisibleConflictsForSection(sectionId);
    conflicts.forEach(conflict => {
      if (!conflict.isMatch) {
        this.selection[conflict.key] = choice;
        this.overriddenFields.add(conflict.key);
      }
    });
  }

  onSelectField(key: string, choice: 'primary' | 'duplicate'): void {
    const conflict = this.conflicts.find(c => c.key === key);
    if (conflict && conflict.isMatch) {
      return;
    }
    this.selection[key] = choice;
    this.overriddenFields.add(key);
  }

  getMergedValue(conflict: { key: string; duplicateValue: any; primaryValue: any }): any {
    return this.selection[conflict.key] === 'duplicate' ? conflict.duplicateValue : conflict.primaryValue;
  }

  getDifferentFieldsCount(): number {
    return this.conflicts.filter(c => !c.isMatch).length;
  }

  getVisibleConflicts(): any[] {
    if (this.showAllFields) {
      return this.conflicts;
    }
    return this.conflicts.filter(c => !c.isMatch);
  }

  toggleShowAllFields(): void {
    this.showAllFields = !this.showAllFields;
    this.refreshConflictCaches();
  }

  swapPrimary(): void {
    const nextNomination = this.primaryNominated === 'primary' ? 'duplicate' : 'primary';
    this.setPrimaryNominated(nextNomination);
  }

  merge(): void {
    if (this.merging || this.loading) {
      return;
    }
    this.dialogService.confirmation(yesNoConfirmation('msg_confirm_contact_merge'))
      .pipe(takeUntil(this.destroy$))
      .subscribe(confirm => {
        if (yesAnswer(confirm)) {
          this.executeMerge();
        }
      });
  }

  private fillMissingValues(target: any, source: any): void {
    if (!target || !source || typeof target !== 'object' || typeof source !== 'object') {
      return;
    }
    Object.keys(source).forEach(key => {
      const srcVal = source[key];
      if (srcVal === null || srcVal === undefined || srcVal === '') {
        return;
      }
      const tgtVal = target[key];
      if (tgtVal === null || tgtVal === undefined || tgtVal === '') {
        target[key] = cloneDeep(srcVal);
      } else if (
        typeof tgtVal === 'object' &&
        typeof srcVal === 'object' &&
        !Array.isArray(tgtVal) &&
        !Array.isArray(srcVal) &&
        !(tgtVal instanceof Date)
      ) {
        this.fillMissingValues(tgtVal, srcVal);
      }
    });
  }

  executeMerge(): void {
    if (this.merging) {
      return;
    }

    const isCol1Primary = this.primaryNominated === 'primary';
    const primaryContact = isCol1Primary ? this.originalPrimaryContact : this.originalDuplicateContact;
    const duplicateContact = isCol1Primary ? this.originalDuplicateContact : this.originalPrimaryContact;

    const mergedContact = cloneDeep(primaryContact);

    // Copy missing sub-DTO structs from duplicate to primary
    if (duplicateContact.customer && !mergedContact.customer) {
      mergedContact.customer = cloneDeep(duplicateContact.customer);
    }
    if (duplicateContact.employee && !mergedContact.employee) {
      mergedContact.employee = cloneDeep(duplicateContact.employee);
    }
    if (duplicateContact.creditorSupplier && !mergedContact.creditorSupplier) {
      mergedContact.creditorSupplier = cloneDeep(duplicateContact.creditorSupplier);
    }
    if (duplicateContact.debtor && !mergedContact.debtor) {
      mergedContact.debtor = cloneDeep(duplicateContact.debtor);
    }
    if (duplicateContact.financeCompany && !mergedContact.financeCompany) {
      mergedContact.financeCompany = cloneDeep(duplicateContact.financeCompany);
    }

    // Automatically fill missing/empty fields on primary contact with non-null values from duplicate contact
    this.fillMissingValues(mergedContact, duplicateContact);

    // Apply explicit choices:
    // If Column 1 is Primary, then choice 'duplicate' (Column 2) overrides Column 1
    // If Column 2 is Primary, then choice 'primary' (Column 1) overrides Column 2
    const overrideChoice = isCol1Primary ? 'duplicate' : 'primary';

    this.conflicts.forEach(conflict => {
      if (this.selection[conflict.key] === overrideChoice) {
        const val = isCol1Primary ? conflict.duplicateRaw : conflict.primaryRaw;
        this.setValueByPath(mergedContact, conflict.key, val);
      }
    });

    const primaryId = isCol1Primary ? this.data.primaryContactId : this.data.duplicateContactId;
    const duplicateId = isCol1Primary ? this.data.duplicateContactId : this.data.primaryContactId;

    const customerDto = this.buildMergeCustomer(mergedContact.customer);
    const employeeDto = this.buildMergeEmployee(mergedContact.employee);
    const creditorDto = this.buildMergeCreditor(mergedContact.creditorSupplier);
    const debtorDto = this.buildMergeDebtor(mergedContact.debtor);
    const financeCompanyDto = this.buildMergeFinanceCompany(mergedContact.financeCompany);

    const mergeData: ContactMergeDto = {
      duplicateId: duplicateId,
      allFieldsMatch: this.allFieldsMatch,
      type: mergedContact.common.type ?? ContactRoleType.Individual,
      firstName: mergedContact.common.individualName?.firstName,
      lastName: mergedContact.common.individualName?.lastName,
      businessName: mergedContact.common.businessName,
      displayNameAs: mergedContact.common.displayNameAs,
      addressee: mergedContact.common.addressee,
      street1: mergedContact.common.street1,
      street2: mergedContact.common.street2,
      city: mergedContact.common.city,
      state: mergedContact.common.state,
      postcode: mergedContact.common.postcode,
      phone1: mergedContact.common.phone1,
      phone2: mergedContact.common.phone2,
      fax: mergedContact.common.fax,
      email: mergedContact.common.email,
      otherEmails: mergedContact.common.otherEmails,
      salutation: mergedContact.common.salutation,
      contact: mergedContact.common.contact,
      memo: mergedContact.common.memo,

      customer: customerDto,
      employee: employeeDto,
      creditorSupplier: creditorDto,
      debtor: debtorDto,
      financeCompany: financeCompanyDto,

      updateCustomer: !!customerDto,
      updateCreditorSupplier: !!creditorDto,
      updateDebtor: !!debtorDto,
      updateEmployee: !!employeeDto,
      updateFinanceCompany: !!financeCompanyDto,
    };

    this.merging = true;
    this.contactService.merge(primaryId, mergeData).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.notificationService.success('msg_contact_merge_success');
        this.dialogRef.close(true);
      },
      error: (error) => {
        this.merging = false;
        this.notificationService.processAll(error);
      }
    });
  }

  close(): void {
    if (this.merging) {
      return;
    }
    this.dialogRef.close();
  }

  private refreshConflictCaches(): void {
    this.visibleConflictsBySection = {};
    this.differentFieldsCountBySection = {};
    this.sections.forEach(sec => {
      this.differentFieldsCountBySection[sec.id] = sec.conflicts.filter(c => !c.isMatch).length;
      this.visibleConflictsBySection[sec.id] = this.showAllFields
        ? sec.conflicts
        : sec.conflicts.filter(c => !c.isMatch);
    });
  }

  private getEnumNameOrString(val: any): any {
    if (val && typeof val === 'object') {
      if ('localization' in val && val.localization) {
        return val.localization;
      }
      if ('name' in val && val.name) {
        return val.name;
      }
      if ('itemCode' in val && val.itemCode) {
        return val.itemCode;
      }
    }
    return val;
  }

  private buildMergeCustomer(customer?: ContactDetailsCustomerDto): Partial<ContactDetailsCustomerDto> | undefined {
    if (!customer) {
      return undefined;
    }
    const result = {
      common: customer.common ? {
        referredBy: customer.common.referredBy ? {
          marketingOptIn: customer.common.referredBy.marketingOptIn,
          referredBy: this.getEnumNameOrString(customer.common.referredBy.referredBy),
          referredDetails: customer.common.referredBy.referredDetails,
          community: customer.common.referredBy.community,
        } : undefined,
        billing: customer.common.billing,
      } : undefined,
      additionalDetails: customer.additionalDetails ? {
        dateOfBirth: customer.additionalDetails.dateOfBirth,
        additionalDatesOfBirth: customer.additionalDetails.additionalDatesOfBirth,
        websiteLead: customer.additionalDetails.websiteLead,
      } : undefined,
      payment: customer.payment,
      terms: customer.terms,
    } as Partial<ContactDetailsCustomerDto>;
    return this.hasAnyValue(result) ? result : undefined;
  }

  private buildMergeEmployee(employee?: ContactDetailsEmployeeDto): Partial<ContactDetailsEmployeeDto> | undefined {
    if (!employee?.common) {
      return undefined;
    }
    const result = {
      common: {
        personal: employee.common.personal ? {
          nameToPrintOnChecks: employee.common.personal.nameToPrintOnChecks,
          taxFileNumber: employee.common.personal.taxFileNumber,
          gender: this.getEnumNameOrString(employee.common.personal.gender),
          dateOfBirth: employee.common.personal.dateOfBirth,
          startDate: employee.common.personal.startDate,
          terminationDate: employee.common.personal.terminationDate,
        } : undefined,
        salary: employee.common.salary ? {
          payrollCycle: this.getEnumNameOrString(employee.common.salary.payrollCycle),
          hoursInPayPeriod: employee.common.salary.hoursInPayPeriod,
          payBasis: this.getEnumNameOrString(employee.common.salary.payBasis),
          salary: employee.common.salary.salary,
          hourlyRate: employee.common.salary.hourlyRate,
          commissionRate: employee.common.salary.commissionRate,
        } : undefined,
      },
      terms: employee.terms,
    } as Partial<ContactDetailsEmployeeDto>;
    return this.hasAnyValue(result) ? result : undefined;
  }

  private buildMergeCreditor(creditor?: ContactDetailsCreditorDto): Partial<ContactDetailsCreditorDto> | undefined {
    if (!creditor) {
      return undefined;
    }
    const type = this.getEnumNameOrString(creditor.common?.type) ?? CreditorType.SubContractor;
    const defaultCategory = type === CreditorType.SubContractor ? SupplierCategoryType.Carpenter : SupplierCategoryType.Hvac;

    const result = {
      common: creditor.common ? {
        type,
        provides: this.getEnumNameOrString(creditor.common.provides) ?? SupplierServiceType.Supply,
        category: this.getEnumNameOrString(creditor.common.category) ?? defaultCategory,
        defaultDeliveryToEmail: creditor.common.defaultDeliveryToEmail,
        orderBy: creditor.common.orderBy,
        billingBy: creditor.common.billingBy,
        scheduleBy: creditor.common.scheduleBy,
        includeExtraDocs: creditor.common.includeExtraDocs,
        businessInfo: creditor.common.businessInfo ? {
          abn: creditor.common.businessInfo.abn,
          gst: creditor.common.businessInfo.gst,
          taxNumber: creditor.common.businessInfo.taxNumber,
          socialNumber: creditor.common.businessInfo.socialNumber,
        } : undefined,
      } : undefined,
      payment: creditor.payment,
      terms: creditor.terms,
    } as Partial<ContactDetailsCreditorDto>;
    return this.hasAnyValue(result) ? result : undefined;
  }

  private buildMergeDebtor(debtor?: ContactDetailsDebtorDto): Partial<ContactDetailsDebtorDto> | undefined {
    if (!debtor) {
      return undefined;
    }
    const common = debtor.common as any;
    const result = {
      common: common ? {
        billing: common.billing,
        creditLimit: common.creditLimit,
        creditLimitNotify: common.creditLimitNotify,
      } : undefined,
      payment: debtor.payment,
      terms: debtor.terms,
    } as Partial<ContactDetailsDebtorDto>;
    return this.hasAnyValue(result) ? result : undefined;
  }

  private buildMergeFinanceCompany(financeCompany?: ContactDetailsFinanceCompanyDto): Partial<ContactDetailsFinanceCompanyDto> | undefined {
    if (!financeCompany) {
      return undefined;
    }
    const common = financeCompany.common as any;
    const result = {
      common: common ? {
        billing: common.billing,
        financeCompanyType: common.financeCompanyType,
      } : undefined,
      payment: financeCompany.payment,
      terms: financeCompany.terms,
    } as Partial<ContactDetailsFinanceCompanyDto>;
    return this.hasAnyValue(result) ? result : undefined;
  }

  private hasAnyValue(obj: any): boolean {
    if (obj === null || obj === undefined) {
      return false;
    }
    if (typeof obj !== 'object') {
      if (typeof obj === 'string') {
        return obj.trim().length > 0;
      }
      return true;
    }
    if (Array.isArray(obj)) {
      return obj.some(item => this.hasAnyValue(item));
    }
    return Object.keys(obj).some(key => this.hasAnyValue(obj[key]));
  }
}
